import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useId,
} from "react";
import styled from "styled-components";
import { Button, Input, Avatar, message, Form, Select } from "antd";
import { AuthContext } from "../context/AuthProvider";
import { useMobileWidgetContext } from "../context/MobileWidgetContext";
import {
  MessageOutlined,
  SendOutlined,
  CloseOutlined,
  BulbOutlined,
  StarOutlined,
  StarFilled,
  BugOutlined,
  ToolOutlined,
  HeartOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { device, useBreakpoints } from "../lib/breakpoints";
import { sendFeedbackForm } from "../helpers/feedback-form";
import { getFeedbackClientContext } from "../helpers/feedback-client-context";
import { trackEvent } from "../helpers/mixpanel";
import Logo from "./general/Logo";

const { TextArea } = Input;
const { Option } = Select;

/**
 * Icon-based stars instead of antd `Rate`: globals.css and other rules often
 * force `.anticon` / `svg` to small fixed sizes; explicit px + !important in
 * `StarRateWrap` keeps tap targets large and predictable.
 */
function FeedbackStarRow({
  value,
  onChange,
  sizePx = 44,
  tooltips = [],
  captionId,
}) {
  const [hoverValue, setHoverValue] = useState(null);
  const rowRef = useRef(null);

  const filled = Number(value) || 0;
  const preview = hoverValue != null ? hoverValue : filled;

  const handleClick = (n) => {
    setHoverValue(null);
    if (filled === n) {
      onChange?.(undefined);
    } else {
      onChange?.(n);
    }
  };

  const handleRowPointerMove = (e) => {
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const star = el?.closest?.("[data-feedback-star]");
    if (star && rowRef.current?.contains(star)) {
      const n = Number(star.getAttribute("data-feedback-star"));
      if (n >= 1 && n <= 5) setHoverValue(n);
    }
  };

  const handleRowPointerLeave = (e) => {
    const next = e.relatedTarget;
    if (next && rowRef.current?.contains(next)) return;
    setHoverValue(null);
  };

  return (
    <StarRateWrap style={{ "--feedback-star-px": `${sizePx}px` }}>
      <div
        ref={rowRef}
        className="feedback-star-row-inner"
        role="radiogroup"
        aria-labelledby={captionId}
        aria-valuenow={filled}
        aria-valuemin={0}
        aria-valuemax={5}
        aria-valuetext={
          filled ? `${filled} of 5 stars` : "No rating selected"
        }
        onPointerMove={handleRowPointerMove}
        onPointerLeave={handleRowPointerLeave}
      >
        {[1, 2, 3, 4, 5].map((n) => {
          const active = n <= preview;
          return (
            <button
              key={n}
              type="button"
              data-feedback-star={n}
              title={tooltips[n - 1]}
              aria-label={
                tooltips[n - 1]
                  ? `${tooltips[n - 1]}, ${n} of 5`
                  : `Rate ${n} of 5`
              }
              onPointerEnter={() => setHoverValue(n)}
              onClick={() => handleClick(n)}
              className="feedback-star-btn"
            >
              {active ? (
                <StarFilled className="feedback-star-glyph feedback-star-glyph--filled" />
              ) : (
                <StarOutlined className="feedback-star-glyph feedback-star-glyph--empty" />
              )}
            </button>
          );
        })}
      </div>
    </StarRateWrap>
  );
}

function FeedbackRatingField({ value, onChange, sizePx, tooltips }) {
  const captionId = useId();

  return (
    <div className="feedback-rating-field">
      <p className="feedback-rating-caption" id={captionId}>
        How would you rate your experience?
      </p>
      <FeedbackStarRow
        captionId={captionId}
        value={value}
        onChange={onChange}
        sizePx={sizePx}
        tooltips={tooltips}
      />
    </div>
  );
}

const FeedbackWidget = ({
  isVisible = true,
  isOpen: externalIsOpen,
  onToggle,
}) => {
  const { user, userInfo } = useContext(AuthContext);
  const { isMobile } = useBreakpoints();
  const { isAIWidgetActive, isNotificationsPanelActive, setActiveWidget, clearActiveWidget } =
    useMobileWidgetContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isFullyOpen, setIsFullyOpen] = useState(false);
  const [isLoadingFading, setIsLoadingFading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  // Use external state if provided, otherwise use internal state
  const currentIsOpen = externalIsOpen !== undefined ? externalIsOpen : isOpen;

  // Sync internal state with external state
  React.useEffect(() => {
    if (externalIsOpen !== undefined) {
      if (externalIsOpen && !isOpen) {
        // Opening the widget
        setIsOpen(true);
        setIsFullyOpen(false);
        setIsLoadingFading(false);

        // Wait for scale animation to complete, then start fade-out
        setTimeout(() => {
          setIsLoadingFading(true);
          // Show content after fade-out animation completes
          setTimeout(() => {
            setIsFullyOpen(true);
            // Focus on first input
            setTimeout(() => {
              inputRef.current?.focus();
            }, 100);
          }, 200);
        }, 250);
      } else if (!externalIsOpen && isOpen) {
        // Closing the widget
        setIsOpen(false);
        setIsFullyOpen(false);
        setIsLoadingFading(false);
        setSuccessfullySubmitted(false);
        form.resetFields();
      }
    }
  }, [externalIsOpen, isOpen, form]);

  const feedbackTypes = [
    {
      value: "feature-request",
      label: "Feature Request",
      icon: <BulbOutlined />,
    },
    { value: "bug-report", label: "Bug Report", icon: <BugOutlined /> },
    { value: "improvement", label: "Improvement", icon: <ToolOutlined /> },
    {
      value: "general-feedback",
      label: "General Feedback",
      icon: <MessageOutlined />,
    },
    { value: "praise", label: "Praise", icon: <HeartOutlined /> },
    { value: "other", label: "Other", icon: <ThunderboltOutlined /> },
  ];

  const toggleWidget = () => {
    if (onToggle) {
      // Use external toggle if provided
      onToggle();
    } else {
      // Use internal state management
      if (!isOpen) {
        // Opening the widget
        setIsOpen(true);
        setIsFullyOpen(false);
        setIsLoadingFading(false);

        // Set this widget as active on mobile
        if (isMobile) {
          setActiveWidget("feedback");
        }

        // Wait for scale animation to complete, then start fade-out
        setTimeout(() => {
          setIsLoadingFading(true);
          // Show content after fade-out animation completes
          setTimeout(() => {
            setIsFullyOpen(true);
            // Focus on first input
            setTimeout(() => {
              inputRef.current?.focus();
            }, 100);
          }, 200);
        }, 250);
      } else {
        // Closing the widget
        setIsOpen(false);
        setIsFullyOpen(false);
        setIsLoadingFading(false);
        setSuccessfullySubmitted(false);
        form.resetFields();

        // Clear active widget on mobile
        if (isMobile) {
          clearActiveWidget();
        }
      }
    }
  };

  const scrollFormToFirstError = (errorInfo) => {
    const first = errorInfo?.errorFields?.[0];
    const name = first?.name;
    if (name != null) {
      form.scrollToField(name, {
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);

    try {
      const clientCtx = await getFeedbackClientContext();

      trackEvent("Feedback Widget Submitted", {
        feedback_type: values.feedbackType,
        has_rating: !!values.rating,
        message_length: values.message?.length || 0,
        user_logged_in: !!user,
        device_type: isMobile ? "mobile" : "desktop",
        has_approx_location: !!(
          clientCtx.geo?.country || clientCtx.geo?.region
        ),
      });

      // Prepare data for API. Use Firebase user when Firestore userInfo is still loading.
      const resolvedEmail =
        userInfo?.email || user?.email || values.email || "";
      const resolvedName =
        userInfo?.firstName && userInfo?.lastName
          ? `${userInfo.firstName} ${userInfo.lastName}`
          : userInfo?.displayName ||
            user?.displayName ||
            values.name ||
            "Anonymous";

      const rawRating = values.rating;
      let ratingOut;
      if (rawRating === undefined || rawRating === null || rawRating === "") {
        ratingOut = undefined;
      } else {
        const n = Number(rawRating);
        ratingOut = Number.isFinite(n) ? n : undefined;
      }

      const feedbackData = {
        name: resolvedName,
        email: resolvedEmail,
        feedbackType: values.feedbackType,
        rating: ratingOut,
        message: values.message,
        userId: user?.uid || "",
        userEmail: resolvedEmail,
        deviceType: isMobile ? "mobile" : "desktop",
        userAgent: clientCtx.userAgent,
        browserLabel: clientCtx.browserLabel,
        locale: clientCtx.locale,
        ...(clientCtx.geo ? { geo: clientCtx.geo } : {}),
      };

      // Submit via feedback form API
      const result = await sendFeedbackForm(feedbackData);

      if (result.success) {
        setSuccessfullySubmitted(true);

        trackEvent("Feedback Widget Success", {
          feedback_type: values.feedbackType,
          rating: values.rating,
        });

        // Reset form after success
        setTimeout(() => {
          form.resetFields();
          setSuccessfullySubmitted(false);
        }, 10000);
      } else {
        // Show specific error message from backend if available
        const errorMessage =
          result.message || "Failed to submit feedback. Please try again.";
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Feedback submission error:", error);
      // Show the actual error message to the user
      const displayMessage =
        error.message ||
        "Sorry, there was an error submitting your feedback. Please try again.";
      message.error(displayMessage);

      trackEvent("Feedback Widget Error", {
        error: error.message,
        feedback_type: values.feedbackType,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <WidgetContainer>
      {/* Floating Button - hidden on mobile when AI widget is active or when this widget is open */}
      {(isMobile
        ? !isAIWidgetActive &&
          !isNotificationsPanelActive &&
          !currentIsOpen
        : true) && (
        <FloatingButton
          type="primary"
          shape="circle"
          size="large"
          icon={currentIsOpen ? <CloseOutlined /> : <StarOutlined />}
          onClick={toggleWidget}
          className="feedback-widget-button"
        />
      )}

      {/* Feedback Widget */}
      {currentIsOpen && (
        <FeedbackWidgetContainer>
          {/* Loading Screen */}
          {!isFullyOpen && (
            <LoadingScreen className={isLoadingFading ? "fade-out" : ""}>
              <LogoContainer>
                <LoadingStarIcon />
              </LogoContainer>
            </LoadingScreen>
          )}

          {/* Feedback Content */}
          <FeedbackContent className={isFullyOpen ? "visible" : "hidden"}>
            <FeedbackHeader>
              <Avatar
                icon={
                  <StarOutlined style={{ filter: "brightness(0) invert(1)" }} />
                }
                style={{
                  backgroundColor: "var(--color-primary-dark)",
                  marginRight: "12px",
                }}
              />
              <div>
                <FeedbackTitle>Share Feedback</FeedbackTitle>
                <FeedbackSubtitle>Help us improve LandlordOps</FeedbackSubtitle>
              </div>
              {/* Mobile close button */}
              <MobileCloseButton onClick={toggleWidget}>
                <CloseOutlined style={{ filter: "brightness(0) invert(1)" }} />
              </MobileCloseButton>
            </FeedbackHeader>

            <FeedbackBody>
              {successfullySubmitted ? (
                <SuccessMessage>
                  <SuccessLogoWrap>
                    <Logo
                      displayMode="icon"
                      linkTo={null}
                      size="large"
                      color="inherit"
                    />
                  </SuccessLogoWrap>
                  <h3>Thanks</h3>
                  <p>We’ve received your feedback.</p>
                </SuccessMessage>
              ) : (
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  onFinishFailed={scrollFormToFirstError}
                  size="large"
                >
                  <Form.Item
                    className="feedback-rating-item"
                    name="rating"
                    colon={false}
                  >
                    <FeedbackRatingField
                      sizePx={isMobile ? 52 : 44}
                      tooltips={[
                        "Poor",
                        "Fair",
                        "Good",
                        "Great",
                        "Excellent",
                      ]}
                    />
                  </Form.Item>

                  <Form.Item
                    name="feedbackType"
                    label="What type of feedback is this?"
                    rules={[
                      {
                        required: true,
                        message: "Please select a feedback type",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select feedback type"
                      ref={inputRef}
                      allowClear
                      getPopupContainer={(trigger) => trigger.parentNode}
                      dropdownStyle={{ borderRadius: "12px" }}
                    >
                      {feedbackTypes.map((type) => (
                        <Option key={type.value} value={type.value}>
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {type.icon}
                            {type.label}
                          </span>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {!user && (
                    <>
                      <Form.Item name="name" label="Your Name (Optional)">
                        <Input placeholder="Enter your name" />
                      </Form.Item>
                      <Form.Item
                        name="email"
                        label="Your Email (Optional)"
                        rules={[
                          {
                            validator: (_, value) => {
                              const v =
                                value === undefined || value === null
                                  ? ""
                                  : String(value).trim();
                              if (!v) return Promise.resolve();
                              const ok =
                                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
                              if (!ok) {
                                return Promise.reject(
                                  new Error("Please enter a valid email")
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input placeholder="Enter your email" type="email" />
                      </Form.Item>
                    </>
                  )}

                  <Form.Item
                    name="message"
                    label="Your Feedback"
                    rules={[
                      { required: true, message: "Please share your feedback" },
                    ]}
                  >
                    <TextArea
                      rows={4}
                      placeholder="Tell us what's on your mind... What's working well? What could be better? Any ideas for new features?"
                      maxLength={1000}
                    />
                  </Form.Item>

                  <Form.Item>
                    <SubmitButton
                      type="primary"
                      htmlType="submit"
                      loading={isSubmitting}
                      icon={
                        <SendOutlined
                          style={{ filter: "brightness(0) invert(1)" }}
                        />
                      }
                    >
                      Send Feedback
                    </SubmitButton>
                  </Form.Item>
                </Form>
              )}
            </FeedbackBody>
          </FeedbackContent>
        </FeedbackWidgetContainer>
      )}
    </WidgetContainer>
  );
};

export default FeedbackWidget;

// Styled Components
const WidgetContainer = styled.div`
  position: fixed;
  bottom: 2rem;
  left: 2rem;
  z-index: 10000002 !important;
  pointer-events: auto;
  animation: slideInLeft 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  @media ${device.mobile} {
    bottom: 1rem;
    left: 1rem;
  }

  @keyframes slideInLeft {
    0% {
      opacity: 0;
      transform: translateX(-30px) scale(0.8);
    }
    100% {
      opacity: 1;
      transform: translateX(0) scale(1);
    }
  }
`;

const FloatingButton = styled(Button)`
  width: 68px !important;
  height: 68px !important;
  min-width: 68px !important;
  min-height: 68px !important;
  max-width: 68px !important;
  max-height: 68px !important;
  border-radius: 50% !important;
  background: #ffffff !important;
  color: var(--color-primary) !important;
  border: 2px solid rgba(44, 95, 45, 0.45) !important;
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.15);
  position: relative;
  overflow: hidden !important;
  padding: 0 !important;
  line-height: 1 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;

  > .ant-btn-icon {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin-inline-end: 0 !important;
    line-height: 0 !important;
  }

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 12px 40px rgba(44, 95, 45, 0.25);
    background: #ffffff !important;
    border-color: var(--color-primary) !important;
    color: var(--color-primary) !important;
  }

  &:active {
    transform: scale(0.95);
  }

  .anticon {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 24px !important;
    line-height: 0 !important;
    margin: 0 !important;
    color: var(--color-primary) !important;
  }

  .anticon svg {
    display: block !important;
    width: 24px !important;
    height: 24px !important;
    color: var(--color-primary) !important;
    fill: var(--color-primary) !important;
  }

  @media ${device.mobile} {
    width: 56px !important;
    height: 56px !important;
    min-width: 56px !important;
    min-height: 56px !important;
    max-width: 56px !important;
    max-height: 56px !important;

    .anticon {
      font-size: 1.25rem !important;
    }

    .anticon svg {
      width: 20px !important;
      height: 20px !important;
    }
  }
`;

const FeedbackWidgetContainer = styled.div`
  position: absolute;
  bottom: 80px;
  left: 0;
  width: 450px;
  height: 600px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(44, 95, 45, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 10000002 !important;

  /* Simple scaling animation from button */
  animation: scaleOpen 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transform-origin: bottom left;

  /* Global overrides for form and dropdown styling */
  .ant-input,
  .ant-input-affix-wrapper,
  .ant-input-group,
  .ant-select-selector,
  .ant-input-number {
    border: 2px solid var(--color-border-light) !important;
    outline: none !important;
    box-shadow: none !important;

    &:focus,
    &:focus-visible,
    &:hover {
      border-color: var(--color-primary) !important;
      box-shadow: 0 0 0 2px var(--color-primary-glow) !important;
      outline: none !important;
    }
  }

  .ant-select,
  .ant-select-selector {
    position: relative !important;
  }

  .ant-select-selector {
    display: flex !important;
    align-items: center !important;
    min-height: 48px !important;
    padding-right: 36px !important;
  }

  .ant-select-arrow {
    position: absolute !important;
    top: 50% !important;
    right: 14px !important;
    transform: translateY(-50%) !important;
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    pointer-events: none !important;

    .anticon {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1rem;
    }
  }

  /* Ensure dropdowns render within panel with rounded corners */
  .ant-select-dropdown {
    border-radius: 12px !important;
    overflow: hidden !important;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12) !important;
  }

  .ant-form-item-label > label {
    font-weight: 600;
    color: var(--color-text);
  }

  @media ${device.mobile} {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    border-radius: 0;
    position: fixed;
    z-index: 10000002 !important;

    @supports not (height: 100dvh) {
      height: calc(100vh - env(keyboard-inset-height, 0px));
    }
  }

  @keyframes scaleOpen {
    0% {
      opacity: 0;
      transform: scale(0.1);
      border-radius: 0;
    }
    100% {
      opacity: 1;
      transform: scale(1);
      border-radius: 0 20px 0 0;
    }
  }
`;

const LoadingScreen = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: opacity 0.3s ease;

  &.fade-out {
    opacity: 0;
    pointer-events: none;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: logoPulse 2s ease-in-out infinite;

  @keyframes logoPulse {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
`;

// The global stylesheet forces `.ant-card-body * { color: #000 !important; }`, which overrides
// inline styles. This component wins with higher specificity + `!important` so the loading
// star always renders in brand green.
const LoadingStarIcon = styled(StarOutlined)`
  && {
    font-size: 120px;
    color: var(--color-primary) !important;
  }

  && svg {
    color: var(--color-primary) !important;
    fill: var(--color-primary) !important;
    stroke: var(--color-primary) !important;
  }
`;

const FeedbackContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: opacity 0.3s ease;

  &.visible {
    opacity: 1;
  }

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
`;

const FeedbackHeader = styled.div`
  background: var(--gradient-primary);
  color: white;
  padding: 1.75rem 1.5rem;
  display: flex;
  align-items: center;
  border-radius: 18px 18px 0 0;
  position: relative;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  @media ${device.mobile} {
    border-radius: 0;
    padding: 1.5rem;
  }
`;

const FeedbackTitle = styled.h3`
  margin: 0;
  font-family: var(--font-primary);
  font-size: 1.25rem;
  font-weight: 700;
  color: white;
  text-align: left;
`;

const FeedbackSubtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  opacity: 0.9;
  color: white;
`;

const MobileCloseButton = styled(Button)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2) !important;
  border: none !important;
  color: white !important;
  width: 32px !important;
  height: 32px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  &:hover {
    background: rgba(255, 255, 255, 0.3) !important;
  }

  @media (min-width: 769px) {
    display: none !important;
  }
`;

const FeedbackBody = styled.div`
  flex: 1;
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  .ant-form-item {
    margin-bottom: 1.1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .ant-form-item-label {
    padding-bottom: 0.125rem;

    > label {
      font-weight: 600;
      color: var(--color-text);
      font-size: 0.95rem;

      &.ant-form-item-required::before {
        color: #ff4d4f;
        margin-right: 0.25rem;
      }
    }
  }

  .feedback-rating-field {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.35rem;
    width: 100%;
  }

  .feedback-rating-caption {
    margin: 0;
    padding: 0;
    font-weight: 600;
    color: var(--color-text);
    font-size: 0.95rem;
    text-align: center;
    line-height: 1.25;
  }

  .ant-select,
  .ant-input {
    border-radius: 8px;
    border: 1px solid var(--color-border-light);
    transition: all 0.2s ease;

    &:hover {
      border-color: var(--color-primary);
    }

    &:focus,
    &.ant-select-focused {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 2px rgba(44, 95, 45, 0.1);
    }
  }

  .ant-input {
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .ant-select {
    line-height: 1 !important;
  }

  .ant-select-selector {
    padding: 0.35rem 0.85rem !important;
    min-height: 36px !important;
    display: flex;
    align-items: center;
  }

  .ant-select-selection-search {
    height: 100% !important;
  }

  .ant-select-selection-placeholder,
  .ant-select-selection-item {
    font-size: 0.92rem !important;
    line-height: 1.4 !important;
    display: flex !important;
    align-items: center !important;
  }

  .ant-select-dropdown {
    .ant-select-item {
      padding: 6px 12px !important;
      font-size: 0.9rem !important;
    }

    .ant-select-item-option-content {
      display: flex !important;
      align-items: center !important;
      gap: 6px !important;
    }

    .ant-select-item-option-active {
      background: rgba(44, 95, 45, 0.08) !important;
    }
  }

  /* Character counter spacing - simple approach */
  .ant-input-textarea-count {
    margin-top: 0.75rem !important;
  }

  .ant-select-selector {
    padding: 0.5rem 1rem;
    min-height: 40px;

    .ant-select-selection-placeholder {
      color: var(--color-text-light);
      font-size: 0.95rem;
    }
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-border-light);
    border-radius: 3px;

    &:hover {
      background: var(--color-text-light);
    }
  }
`;

const SuccessLogoWrap = styled.div`
  margin-bottom: 1rem;

  img {
    display: block;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  padding: 2rem;

  h3 {
    color: var(--color-text);
    margin-bottom: 0.5rem;
    font-family: var(--font-primary);
    font-weight: 600;
    font-size: 1.25rem;
  }

  p {
    color: var(--color-text-light);
    line-height: 1.5;
    max-width: 300px;
    margin: 0;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  height: 48px;
  background: var(--gradient-primary) !important;
  border: none !important;
  font-weight: 600;
  font-size: 1rem;
  border-radius: 8px;
  margin-top: 0.5rem;
  transition:
    background 0.2s ease,
    box-shadow 0.2s ease;

  &:hover:not(:disabled):not(.ant-btn-loading) {
    background: var(--gradient-primary) !important;
    filter: brightness(0.97);
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.2);
  }

  &:active:not(:disabled):not(.ant-btn-loading) {
    filter: brightness(0.93);
  }

  .anticon {
    font-size: 0.9rem;
  }
`;

const StarRateWrap = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 0.2rem 0 0;

  .feedback-star-row-inner {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: clamp(8px, 2vw, 14px);
    touch-action: pan-y;
  }

  .feedback-star-btn {
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 6px 4px;
    line-height: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    touch-action: manipulation;
  }

  .feedback-star-btn:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }

  /* Match LoadingStarIcon workaround: globals can shrink .anticon svg - force px. */
  .feedback-star-glyph.anticon {
    font-size: var(--feedback-star-px, 44px) !important;
    line-height: 1 !important;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
  }

  .feedback-star-glyph.anticon svg {
    width: var(--feedback-star-px, 44px) !important;
    height: var(--feedback-star-px, 44px) !important;
  }

  .feedback-star-glyph--empty.anticon {
    color: rgba(0, 0, 0, 0.26) !important;
  }

  .feedback-star-glyph--empty.anticon svg {
    fill: currentColor !important;
  }

  .feedback-star-glyph--filled.anticon {
    color: #fadb14 !important;
  }

  .feedback-star-glyph--filled.anticon svg {
    fill: #fadb14 !important;
  }

  @media ${device.mobile} {
    .feedback-star-glyph.anticon {
      font-size: var(--feedback-star-px, 52px) !important;
    }

    .feedback-star-glyph.anticon svg {
      width: var(--feedback-star-px, 52px) !important;
      height: var(--feedback-star-px, 52px) !important;
    }
  }
`;
